import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RFQ, JobCategory } from '@/lib/types/job';
import { errorTracking } from '@/lib/errorTracking';

/**
 * Options for filtering vendor bids
 */
interface UseVendorBidsOptions {
  /** Search bids by job title or description */
  search?: string;
  /** Filter bids by job category */
  category?: JobCategory;
}

/**
 * Extended RFQ type with bid information
 */
export interface RFQWithBid extends RFQ {
  job: any;
  bid: {
    id: string;
    price_cents: number;
    currency: string;
    lead_time_days: number;
    status: string;
    created_at: string;
    notes?: string;
  };
  bid_line_items?: Array<{
    id: string;
    total_price_cents: number;
    unit_price_cents: number;
    quantity_option: number;
  }>;
  bids?: any[]; // Other bids for context
}

/**
 * Hook for fetching vendor's submitted bids on RFQs
 * 
 * Fetches RFQs where the vendor has submitted bids but hasn't been awarded yet.
 * Excludes RFQs that have been awarded (those appear in Active Jobs).
 * 
 * @param options - Filtering options
 * @returns Object with bids array, loading state, error, and refetch function
 * 
 * @example
 * ```typescript
 * const { bids, loading, refetch } = useVendorBids({
 *   category: 'print_materials',
 *   search: 'business cards'
 * });
 * ```
 */
export const useVendorBids = (options: UseVendorBidsOptions = {}) => {
  const [bids, setBids] = useState<RFQWithBid[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBids = async () => {
    let user: any = null;
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      user = currentUser;
      if (!user) {
        setBids([]);
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      // Get user's profile to check org_role via RPC
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile) {
        throw new Error('Profile not found');
      }

      // Fetch org role using RPC function
      const { data: orgRole } = await supabase
        .rpc('get_user_org_role', {
          _user_id: user.id,
          _org_id: profile.organization_id
        } as any);

      // Fetch vendor's bids with RFQ and job details
      // Members: only see their own bids
      // Owners/Super Users: see all org bids
      let query = supabase
        .from('bids')
        .select(`
          id,
          vendor_user_id,
          lead_time_days,
          status,
          created_at,
          notes,
          rfq_id,
          rfqs!inner(
            *,
            job:jobs(
              *,
              organization:organizations(id, name)
            )
          ),
          bid_line_items(
            id,
            specification_id,
            quantity_option,
            unit_price_cents,
            total_price_cents,
            lead_time_days
          )
        `)
        .order('created_at', { ascending: false });

      // Apply role-based filtering
      if (orgRole === 'member') {
        // Members can only see their own bids
        query = query.eq('vendor_user_id', user.id);
      } else if (orgRole === 'owner' || orgRole === 'super_user') {
        // Owners and Super Users can see all bids from their organization
        const { data: orgUsers } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('organization_id', profile.organization_id);
        
        if (orgUsers && orgUsers.length > 0) {
          const userIds = orgUsers.map(u => u.user_id);
          query = query.in('vendor_user_id', userIds);
        }
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('[useVendorBids] Fetch error:', fetchError);
        console.error('[useVendorBids] Error details:', {
          message: fetchError.message,
          details: fetchError.details,
          hint: fetchError.hint,
          code: fetchError.code
        });
        console.error('[useVendorBids] User ID:', user.id);
        console.error('[useVendorBids] Query filters:', { 
          search: options.search, 
          category: options.category 
        });
        
        // Pass the actual Supabase error message to the UI
        throw new Error(fetchError.message || fetchError.details || 'Failed to fetch bids');
      }

      let filteredData = data || [];

      // Filter out awarded jobs (those appear in Active Jobs)
      filteredData = filteredData.filter((bid: any) => {
        const rfq = bid.rfqs;
        if (!rfq || !rfq.job) return false;
        
        // Exclude jobs that have been awarded or are in progress
        const excludedStatuses = [
          'awarded',
          'in_progress',
          'completed_pending_review',
          'accepted',
          'rejected',
          'disputed',
          'closed'
        ];
        
        return !excludedStatuses.includes(rfq.job.status);
      });

      // Apply category filter
      if (options.category) {
        filteredData = filteredData.filter((bid: any) => 
          bid.rfqs?.job?.category === options.category
        );
      }

      // Apply search filter
      if (options.search) {
        const searchLower = options.search.toLowerCase();
        filteredData = filteredData.filter((bid: any) => {
          const rfq = bid.rfqs;
          return rfq?.job && (
            rfq.job.title.toLowerCase().includes(searchLower) ||
            rfq.job.description.toLowerCase().includes(searchLower)
          );
        });
      }

      // Transform data to include bid info at top level
      const transformedData: RFQWithBid[] = filteredData.map((bid: any) => {
        const rfq = bid.rfqs;
        
        // Calculate total price from all line items
        const totalPrice = (bid.bid_line_items || []).reduce(
          (sum: number, item: any) => sum + (item.total_price_cents || 0),
          0
        );
        
        // Calculate max lead time from line items (actual vendor input)
        const maxLeadTimeDays = (bid.bid_line_items || []).reduce(
          (max: number, item: any) => Math.max(max, item.lead_time_days || 0),
          0
        );
        
        return {
          ...rfq,
          bid: {
            id: bid.id,
            lead_time_days: maxLeadTimeDays || bid.lead_time_days,  // Use line items, fallback to bid-level
            status: bid.status,
            created_at: bid.created_at,
            notes: bid.notes,
            price_cents: totalPrice,
            currency: 'USD'  // System default, currency not stored at bid level
          }
        };
      });

      setBids(transformedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching vendor bids:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch bids';
      
      // Report to error tracking system
      if (err instanceof Error) {
        errorTracking.captureError(err, {
          context: 'useVendorBids',
          userId: user?.id,
          filters: options
        });
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBids();
  }, [options.search, options.category]);

  return { bids, loading, error, refetch: fetchBids };
};
